import React, { useState, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Users, Phone, Mail, MapPin, 
  DollarSign, User, MessageSquare, CheckCircle, 
  XCircle, AlertCircle, ChevronLeft, ChevronRight
} from 'lucide-react';

// Format helpers
const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price || 0);
};

const formatTime12hr = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Status colors for calendar events
const getStatusColor = (status, paymentStatus) => {
  if (status === 'cancelled') return { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' };
  if (paymentStatus === 'paid') return { bg: '#dcfce7', border: '#22c55e', text: '#166534' };
  if (paymentStatus === 'pending') return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
  return { bg: '#e0e7ff', border: '#6366f1', text: '#3730a3' };
};

export default function BookingCalendarView({ bookings = [], onNavigateToBooking, onRefresh }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [dayBookings, setDayBookings] = useState([]);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);

  // Convert bookings to FullCalendar events
  const events = useMemo(() => {
    return bookings.map(booking => {
      const colors = getStatusColor(booking.status, booking.payment_status);
      const bookingDate = booking.date || booking.tour_date;
      const bookingTime = booking.time || '09:00';
      
      return {
        id: booking.id,
        title: `${booking.tour_title} - ${booking.booker_name || 'Guest'}`,
        start: bookingDate ? `${bookingDate}T${bookingTime}` : new Date().toISOString(),
        end: bookingDate ? `${bookingDate}T${bookingTime}` : new Date().toISOString(),
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: colors.text,
        extendedProps: {
          booking: booking,
          guests: booking.guests,
          status: booking.status,
          paymentStatus: booking.payment_status,
          totalPrice: booking.total_price,
          customerName: booking.booker_name || 'Guest',
          customerPhone: booking.booker_phone,
          customerEmail: booking.booker_email || booking.user_email,
          tourTitle: booking.tour_title,
          referenceNumber: booking.reference_number
        }
      };
    });
  }, [bookings]);

  // Handle date click - show all bookings for that day
  const handleDateClick = (info) => {
    const clickedDate = info.dateStr;
    const bookingsOnDay = bookings.filter(b => {
      const bookingDate = b.date || b.tour_date;
      return bookingDate === clickedDate;
    });
    
    setSelectedDate(clickedDate);
    setDayBookings(bookingsOnDay);
    setDayDialogOpen(true);
  };

  // Handle event click - show booking details
  const handleEventClick = (info) => {
    const booking = info.event.extendedProps.booking;
    setSelectedBooking(booking);
  };

  // Render event content with more details
  const renderEventContent = (eventInfo) => {
    const { guests, status, paymentStatus } = eventInfo.event.extendedProps;
    
    return (
      <div className="p-1 overflow-hidden">
        <div className="font-semibold text-xs truncate">{eventInfo.event.title}</div>
        <div className="flex items-center gap-1 text-xs opacity-80">
          <Users className="h-3 w-3" />
          <span>{guests}</span>
          {paymentStatus === 'paid' && <CheckCircle className="h-3 w-3 ml-1" />}
          {status === 'cancelled' && <XCircle className="h-3 w-3 ml-1" />}
        </div>
      </div>
    );
  };

  // Get stats for the header
  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const todayBookings = bookings.filter(b => (b.date || b.tour_date) === today);
    const confirmedCount = bookings.filter(b => b.status === 'confirmed' || b.payment_status === 'paid').length;
    const pendingCount = bookings.filter(b => b.payment_status === 'pending' && b.status !== 'cancelled').length;
    const cancelledCount = bookings.filter(b => b.status === 'cancelled').length;
    
    return { todayBookings: todayBookings.length, confirmed: confirmedCount, pending: pendingCount, cancelled: cancelledCount };
  }, [bookings]);

  return (
    <div className="space-y-4" data-testid="booking-calendar-view">
      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="p-3 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-xs text-blue-700">Today</p>
              <p className="text-xl font-bold text-blue-600">{stats.todayBookings}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 bg-green-50 border-green-200">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-xs text-green-700">Confirmed</p>
              <p className="text-xl font-bold text-green-600">{stats.confirmed}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <div>
              <p className="text-xs text-amber-700">Pending</p>
              <p className="text-xl font-bold text-amber-600">{stats.pending}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 bg-red-50 border-red-200">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <div>
              <p className="text-xs text-red-700">Cancelled</p>
              <p className="text-xl font-bold text-red-600">{stats.cancelled}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-muted-foreground">Legend:</span>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-200 border border-green-500" />
          <span>Paid</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-amber-200 border border-amber-500" />
          <span>Pending</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-red-200 border border-red-500" />
          <span>Cancelled</span>
        </div>
      </div>

      {/* Calendar */}
      <Card className="p-4 rounded-xl">
        <style>{`
          .fc {
            --fc-border-color: #e5e7eb;
            --fc-button-bg-color: #f3f4f6;
            --fc-button-border-color: #d1d5db;
            --fc-button-text-color: #374151;
            --fc-button-hover-bg-color: #e5e7eb;
            --fc-button-hover-border-color: #9ca3af;
            --fc-button-active-bg-color: #d1d5db;
            --fc-today-bg-color: #fef3c7;
          }
          .fc .fc-button-primary {
            font-weight: 500;
            border-radius: 0.375rem;
          }
          .fc .fc-button-primary:not(:disabled).fc-button-active,
          .fc .fc-button-primary:not(:disabled):active {
            background-color: #374151;
            border-color: #374151;
            color: white;
          }
          .fc .fc-toolbar-title {
            font-size: 1.25rem;
            font-weight: 600;
          }
          .fc .fc-daygrid-day-number {
            font-weight: 500;
            padding: 4px 8px;
          }
          .fc .fc-daygrid-day.fc-day-today {
            background-color: #fef9c3 !important;
          }
          .fc .fc-event {
            cursor: pointer;
            border-radius: 4px;
            font-size: 0.75rem;
            margin-bottom: 1px;
          }
          .fc .fc-daygrid-event {
            white-space: normal;
          }
          .fc .fc-col-header-cell-cushion {
            font-weight: 600;
            color: #374151;
          }
        `}</style>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,listWeek'
          }}
          events={events}
          eventContent={renderEventContent}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          height="auto"
          dayMaxEvents={3}
          moreLinkClick="popover"
          nowIndicator={true}
          weekends={true}
          fixedWeekCount={false}
          showNonCurrentDates={true}
          buttonText={{
            today: 'Today',
            month: 'Month',
            week: 'Week',
            list: 'List'
          }}
        />
      </Card>

      {/* Day Bookings Dialog */}
      <Dialog open={dayDialogOpen} onOpenChange={setDayDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Bookings for {selectedDate ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { 
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
              }) : ''}
            </DialogTitle>
            <DialogDescription>
              {dayBookings.length} booking{dayBookings.length !== 1 ? 's' : ''} scheduled
            </DialogDescription>
          </DialogHeader>
          
          {dayBookings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No bookings scheduled for this date</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dayBookings.map((booking) => (
                <Card 
                  key={booking.id} 
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setDayDialogOpen(false);
                    setSelectedBooking(booking);
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold">{booking.tour_title}</h4>
                      <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                        <User className="h-4 w-4" />
                        {booking.booker_name || 'Guest'}
                        <span className="mx-1">•</span>
                        <Users className="h-4 w-4" />
                        {booking.guests} guest{booking.guests !== 1 ? 's' : ''}
                      </p>
                      <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                        <Clock className="h-4 w-4" />
                        {formatTime12hr(booking.time)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{formatPrice(booking.total_price)}</p>
                      <Badge className={`mt-1 ${
                        booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        booking.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {booking.status === 'cancelled' ? 'Cancelled' : 
                         booking.payment_status === 'paid' ? 'Paid' : 'Pending'}
                      </Badge>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking Detail Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={(open) => !open && setSelectedBooking(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Booking Details
            </DialogTitle>
            <DialogDescription>
              Reference: #{selectedBooking?.reference_number || 'N/A'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              {/* Status & Price */}
              <div className="flex justify-between items-center pb-4 border-b">
                <Badge className={`text-sm ${
                  selectedBooking.status === 'cancelled' ? 'bg-red-500' :
                  selectedBooking.payment_status === 'paid' ? 'bg-green-500' :
                  'bg-amber-500'
                } text-white`}>
                  {selectedBooking.status === 'cancelled' ? 'CANCELLED' : 
                   selectedBooking.payment_status?.toUpperCase() || 'PENDING'}
                </Badge>
                <span className="text-2xl font-bold text-green-600">
                  {formatPrice(selectedBooking.total_price)}
                </span>
              </div>
              
              {/* Tour Info */}
              <div className="space-y-2">
                <h4 className="font-semibold text-lg">{selectedBooking.tour_title}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{new Date(selectedBooking.date || selectedBooking.tour_date).toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric'
                    })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{formatTime12hr(selectedBooking.time)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedBooking.guests} guest{selectedBooking.guests !== 1 ? 's' : ''}</span>
                  </div>
                  {selectedBooking.pickup_hotel && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="truncate">{selectedBooking.pickup_hotel}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Customer Info */}
              <Card className="p-3 bg-muted/50">
                <h5 className="font-medium mb-2 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer
                </h5>
                <div className="space-y-1 text-sm">
                  <p className="font-semibold">{selectedBooking.booker_name || 'Guest'}</p>
                  {(selectedBooking.booker_email || selectedBooking.user_email) && (
                    <p className="text-muted-foreground">{selectedBooking.booker_email || selectedBooking.user_email}</p>
                  )}
                  {selectedBooking.booker_phone && (
                    <p className="text-muted-foreground">{selectedBooking.booker_phone}</p>
                  )}
                </div>
              </Card>
              
              {/* Quick Contact Buttons */}
              <div className="flex gap-2 pt-2">
                {(selectedBooking.booker_email || selectedBooking.user_email) && (
                  <a href={`mailto:${selectedBooking.booker_email || selectedBooking.user_email}?subject=Re: Booking ${selectedBooking.reference_number}`} className="flex-1">
                    <Button variant="outline" className="w-full gap-2">
                      <Mail className="h-4 w-4" /> Email
                    </Button>
                  </a>
                )}
                {selectedBooking.booker_phone && (
                  <>
                    <a href={`sms:${selectedBooking.booker_phone}`} className="flex-1">
                      <Button variant="outline" className="w-full gap-2">
                        <MessageSquare className="h-4 w-4" /> SMS
                      </Button>
                    </a>
                    <a href={`https://wa.me/${selectedBooking.booker_phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button className="w-full gap-2 bg-green-600 hover:bg-green-700">
                        <MessageSquare className="h-4 w-4" /> WhatsApp
                      </Button>
                    </a>
                  </>
                )}
              </div>
              
              {/* View Full Details Button */}
              {onNavigateToBooking && (
                <Button 
                  variant="outline" 
                  className="w-full mt-2"
                  onClick={() => {
                    setSelectedBooking(null);
                    onNavigateToBooking(selectedBooking.id);
                  }}
                >
                  View Full Booking Details
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
